// Book data utilities

// Strip ephemeral fields before saving to DynamoDB
// photoUrl (signed, expires) and photoBase64 (large) are re-resolved from photoPath on load
export const cleanPagesForSave = (pages) => pages.map(({ photoUrl, photoBase64, ...rest }) => rest);

// Build the active book's page list after a newly-saved story, ORPHAN-SAFELY.
//
// Why this exists: the interview save path used to build `[...stBookPages, newPage]`.
// `stBookPages` is React state, which lags a save that completed earlier in the same
// tick (booksRef.current updates synchronously; React state does not), so a rapid
// second save persisted a stale list MISSING the first save's page — the first
// story's LegacyEntry survived while its book page vanished (an ORPHAN that nags the
// recovery modal). The written-story path (handleStSaveWritten) already avoided this
// by reading pages from booksRef.current; this makes the ref the base for all paths.
//
//  - refPages: booksRef.current[activeBook].pages — AUTHORITATIVE (reflects prior
//    same-tick saves) and already cleaned for save. Establishes which pages exist
//    and their order; never dropped.
//  - uiPages:  stBookPages — may be stale, but carries the rich in-session fields
//    (photoBase64/photoUrl) the PDF preview needs. Overlaid as VALUES onto the ref
//    order; a UI-only page (not yet in the ref) is preserved (never drop user content).
//  - newPage:  the page for the just-saved story, appended last (newest chapter last).
export const buildUpdatedBookPages = (refPages, uiPages, newPage) => {
  const byId = new Map();
  for (const p of refPages || []) {
    if (p && p.storyId != null) byId.set(p.storyId, p);
  }
  for (const p of uiPages || []) {
    if (!p || p.storyId == null) continue;
    const existing = byId.get(p.storyId);
    byId.set(p.storyId, existing ? { ...existing, ...p } : p);
  }
  if (newPage && newPage.storyId != null) {
    byId.delete(newPage.storyId); // ensure the new page lands LAST, and never duplicates
    byId.set(newPage.storyId, newPage);
  }
  return [...byId.values()];
};

// Lulu hardcover minimum page count
export const LULU_MIN_PAGES = 24;

// F-251: the pages that will ACTUALLY print.
//
// Every render and the PDF build resolve a page through `stories.find(s => s.id === page.storyId)`
// and skip it on a miss (BookBuilderPage.js:1933-1934, then `.filter(Boolean)` at 1953). A page
// whose story was deleted therefore contributes NOTHING to the printed book — but the page/price
// estimate counted the RAW array, so a customer with dangling pages was quoted for chapters that
// could never print. 17 such pages existed across 9 accounts when this was found (F-250).
//
// LOAD GUARD: `stories` is empty for the first frames after mount. Filtering against an empty set
// would report zero chapters and flash a wrong price (and a false "needs more pages"), so an empty
// stories list falls back to the raw count — the pre-F-251 behaviour — rather than lying downward.
// The estimate is a live approximation either way; checkout re-reads the real rendered page count
// from the generated PDF (BookBuilderPage.js:1974-1995).
export const resolvablePages = (pages, stories) => {
  if (!Array.isArray(pages)) return [];
  if (!Array.isArray(stories) || stories.length === 0) return pages;
  const ids = new Set(stories.map((s) => s && s.id).filter(Boolean));
  return pages.filter((p) => p && p.storyId && ids.has(p.storyId));
};

export const resolvablePageCount = (pages, stories) => resolvablePages(pages, stories).length;

// ─── F-252 TOMBSTONES ────────────────────────────────────────────────────────────────────────
// A page removal cannot be expressed by simply dropping the page: mergeBooks unions the local and
// server page lists, so the server's copy re-adds it on the next write (that is how the 17 dangling
// pages in F-250 were created). A removal is recorded as an ADD to the book's `removedStoryIds`,
// which the union DOES propagate, and mergeBooks subtracts tombstones after the union.
//
// These are pure so the removal contract is testable without React or AWS.

const tombOf = (b) => (Array.isArray(b && b.removedStoryIds) ? b.removedStoryIds : []);
const pagesOf = (b) => (Array.isArray(b && b.pages) ? b.pages : []);

const tombstoneBook = (b, storyId) => ({
  ...b,
  pages: pagesOf(b).filter((p) => !(p && p.storyId === storyId)),
  removedStoryIds: tombOf(b).includes(storyId) ? tombOf(b) : [...tombOf(b), storyId],
});

// DELETE FOREVER: the LegacyEntry is gone, so its page must disappear from EVERY book — not just
// the active one. saveBookData writes a single book by construction, which is why this returns a
// whole books array for persistBooks. Tombstoning even books that did not hold the page is
// deliberate: another device may have a page this device never saw, and the union would re-add it.
export const withStoryRemovedEverywhere = (books, storyId) => {
  if (!Array.isArray(books) || !storyId) return Array.isArray(books) ? books : [];
  return books.map((b) => tombstoneBook(b, storyId));
};

// REMOVE FROM THIS BOOK: non-destructive — the story stays in the library and may be re-added
// later, so only this book is tombstoned. The tombstone is cleared on re-add (see clearTombstones).
export const withStoryRemovedFromBook = (books, bookId, storyId) => {
  if (!Array.isArray(books) || !bookId || !storyId) return Array.isArray(books) ? books : [];
  return books.map((b) => (b && b.id === bookId ? tombstoneBook(b, storyId) : b));
};

// Any storyId explicitly PRESENT in a page list is an intentional (re-)add, so its tombstone is
// dropped. Applied at the single saveBookData choke point, so every add path clears without each
// one having to remember to — a rule nobody has to apply is a rule that cannot drift.
export const clearTombstones = (book, pages) => {
  const tomb = tombOf(book);
  if (!tomb.length || !Array.isArray(pages)) return book;
  const present = new Set(pages.map((p) => p && p.storyId).filter(Boolean));
  const next = tomb.filter((id) => !present.has(id));
  return next.length === tomb.length ? book : { ...book, removedStoryIds: next };
};

// Estimate print pages and pricing for a book
export const stBookEstimate = (chapters) => {
  const contentPages = 3 + (chapters * 3);
  const printPages = contentPages % 2 === 0 ? contentPages : contentPages + 1;
  const luluPages = Math.max(printPages, LULU_MIN_PAGES);
  const luluCost = 9.50 + (luluPages * 0.07);
  const cost = luluCost + 1.75;
  const price = Math.ceil(cost * 2.0);
  const needsMorePages = printPages < LULU_MIN_PAGES;
  const pagesNeeded = needsMorePages ? LULU_MIN_PAGES - printPages : 0;
  return { printPages, luluPages, price: price.toFixed(2), needsMorePages, pagesNeeded };
};

// ── Enhanced-photo key convention (F-311) ────────────────────────────────────────────────
//
// The enhancer names its output `<original>_preview.<ext>` — RemnantTopazApi's `buildEnhanceKey`
// is called with the '_preview' suffix by the only action the book builder invokes
// (`preview_enhance`). This is the ONE place the client should encode that fact.
//
// It exists because REVERT cannot rely on `originalPhotoPath` being stored. That field is written
// onto the page by `runEnhancement`, and until F-301 every page-field write was silently discarded
// by the merge — so for any photo enhanced before that fix, the pointer back to the original does
// not exist. The first customer's entire book is in exactly that state. Deriving the original from
// the enhanced key is what makes revert work on the books that actually need it, rather than only
// on photos enhanced after the fix.
export const ENHANCED_SUFFIX = '_preview';

// Returns the original S3 key for an enhanced key, or null if this is not an enhanced key.
// Null means "no opinion" — callers fall back rather than guessing a path that may not exist.
export const originalKeyFromEnhanced = (key) => {
  if (typeof key !== 'string' || !key) return null;
  const dot = key.lastIndexOf('.');
  const stem = dot === -1 ? key : key.slice(0, dot);
  const ext = dot === -1 ? '' : key.slice(dot);
  if (!stem.endsWith(ENHANCED_SUFFIX)) return null;
  return stem.slice(0, -ENHANCED_SUFFIX.length) + ext;
};
